import React, { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';

import * as s from './styled-changelog';

const FILTERS = [
  'Tudo',
  'Features',
  'Correções',
  'Estilo / UI',
  'Performance',
  'Refactor',
];

const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  let offset = 0;
  return parts.map((part) => {
    const key = `${offset}-${part}`;
    offset += part.length;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
};

const ChangelogView = ({ currentVersion, description, releases, title }) => {
  const [filter, setFilter] = useState('Tudo');

  const visibleFilters = useMemo(() => {
    const groupTitles = new Set(
      releases.flatMap((release) => release.groups.map((group) => group.title))
    );

    return FILTERS.filter((item) => item === 'Tudo' || groupTitles.has(item));
  }, [releases]);

  const filteredReleases = useMemo(() => {
    if (filter === 'Tudo') return releases;
    return releases
      .map((release) => ({
        ...release,
        groups: release.groups.filter((group) => group.title === filter),
      }))
      .filter((release) => release.groups.length > 0);
  }, [filter, releases]);

  const totalChanges = releases.reduce(
    (total, release) =>
      total +
      release.groups.reduce(
        (groupTotal, group) => groupTotal + group.items.length,
        0
      ),
    0
  );

  return (
    <s.Container>
      <s.Header>
        <div>
          <s.Eyebrow>Centro de Controle Operacional</s.Eyebrow>
          <s.Title>{title}</s.Title>
          <s.Description>{description}</s.Description>
          <s.PlatformTabs aria-label="Selecionar changelog">
            <NavLink exact to="/changelog" activeClassName="active">
              Front-end
            </NavLink>
            <NavLink to="/changelog/backend" activeClassName="active">
              Back-end
            </NavLink>
          </s.PlatformTabs>
        </div>

        <s.Stats>
          <s.Stat>
            <small>Versão atual</small>
            <strong>v{currentVersion}</strong>
          </s.Stat>
          <s.Stat>
            <small>Releases</small>
            <strong>{releases.length}</strong>
          </s.Stat>
          <s.Stat>
            <small>Mudanças</small>
            <strong>{totalChanges}</strong>
          </s.Stat>
        </s.Stats>
      </s.Header>

      <s.Filters aria-label="Filtrar changelog">
        {visibleFilters.map((item) => (
          <s.FilterButton
            key={item}
            type="button"
            $active={filter === item}
            onClick={() => setFilter(item)}
          >
            {item}
          </s.FilterButton>
        ))}
      </s.Filters>

      <s.Timeline>
        {filteredReleases.length === 0 && (
          <s.Empty>Nenhuma mudança encontrada para o filtro selecionado.</s.Empty>
        )}

        {filteredReleases.map((release) => (
          <s.Release key={release.version}>
            <s.ReleaseHeader>
              <h2>v{release.version}</h2>
              <time dateTime={release.date}>
                {new Date(`${release.date}T00:00:00`).toLocaleDateString(
                  'pt-BR'
                )}
              </time>
            </s.ReleaseHeader>
            <s.GroupGrid>
              {release.groups.map((group) => (
                <s.Group key={`${release.version}-${group.title}`}>
                  <h3>{group.title}</h3>
                  <ul>
                    {group.items.map((item) => (
                      <li key={`${release.version}-${group.title}-${item}`}>
                        {renderInline(item)}
                      </li>
                    ))}
                  </ul>
                </s.Group>
              ))}
            </s.GroupGrid>
          </s.Release>
        ))}
      </s.Timeline>
    </s.Container>
  );
};

export default ChangelogView;
